import {forwardRef, useEffect, useImperativeHandle, useRef} from "react";
import type {CSSProperties, ReactNode} from "react";
import {colors, fonts} from "../../theme/tokens";
import "./PanCanvas.css";

// A pan/zoom viewport: a clipping root with one transformed content layer, a zoom HUD, and an optional dot grid that
// scales with the content. The content is the caller's. The transform is applied to the layer node directly, and the
// view lives in refs, so a drag or zoom stream never re-renders React; the wheel listener is not passive so that
// wheel-zoom can prevent page scrolling.

export interface PanCanvasView {
    scale: number;
    translateX: number;
    translateY: number;
}

export interface PanCanvasHandle {
    /** Scales the content to fit (at most 1x) and centers it. */
    fit(): void;
    /** Zooms about the viewport center: in by 1.25x when `direction > 0`, otherwise out. */
    zoomStep(direction: number): void;
    readonly view: PanCanvasView;
}

export interface PanCanvasLabels {
    zoomOut: string;
    zoomIn: string;
    fit: string;
}

export interface PanCanvasProps {
    children: ReactNode;
    labels: PanCanvasLabels;
    /** Dot grid that moves and scales with the content. */
    grid?: boolean;
    /** Disables pan, zoom and the HUD, for example behind an overlay. */
    frozen?: boolean;
    minScale?: number;
    maxScale?: number;
    /** Grid tile size in px at scale 1. */
    gridSize?: number;
    hud?: boolean;
    /** Caption in the bottom-right corner. */
    hint?: ReactNode;
    layerStyle?: CSSProperties;
    style?: CSSProperties;
}

const TOKEN_VARIABLES: CSSProperties = {
    "--pc-background": colors.canvas,
    "--pc-surface": colors.card,
    "--pc-line": colors.cardBorder,
    "--pc-line-strong": colors.inputBorder,
    "--pc-ink": colors.ink,
    "--pc-ink-soft": colors.body,
    "--pc-ink-muted": colors.muted,
    "--pc-ink-dim": colors.faint,
    "--pc-focus": colors.primary,
    "--pc-font-sans": fonts.sans,
    "--pc-font-mono": fonts.mono,
};

/** A click that travels further than this many pixels is a pan, and the click it ends in is swallowed. */
const DRAG_THRESHOLD_PX = 4;

export const PanCanvas = forwardRef<PanCanvasHandle, PanCanvasProps>(function PanCanvas(
        {children, labels, grid = true, frozen = false, minScale = 0.2, maxScale = 4, gridSize = 24, hud = true, hint, layerStyle, style},
        ref,
) {
    const rootRef = useRef<HTMLDivElement>(null);
    const layerRef = useRef<HTMLDivElement>(null);
    const zoomReadoutRef = useRef<HTMLSpanElement>(null);
    const viewRef = useRef<PanCanvasView>({scale: 1, translateX: 0, translateY: 0});
    // Read inside the DOM listeners, so freezing takes effect without binding them again.
    const frozenRef = useRef(frozen);
    frozenRef.current = frozen;
    const controllerRef = useRef<PanCanvasHandle | null>(null);

    useEffect(() => {
        const root = rootRef.current;
        const layer = layerRef.current;
        if (!root || !layer) return;
        const view = viewRef.current;

        const apply = (): void => {
            layer.style.transform = `translate(${view.translateX}px,${view.translateY}px) scale(${view.scale})`;
            root.style.setProperty("--pc-grid-size", `${gridSize * view.scale}px`);
            root.style.setProperty("--pc-grid-x", `${view.translateX}px`);
            root.style.setProperty("--pc-grid-y", `${view.translateY}px`);
            // Read on every apply: the HUD can mount after this effect, for example once the content has loaded.
            const zoomReadout = zoomReadoutRef.current;
            if (zoomReadout) zoomReadout.textContent = `${Math.round(view.scale * 100)}%`;
        };
        const zoomAt = (centerX: number, centerY: number, nextScale: number): void => {
            const scale = Math.max(minScale, Math.min(maxScale, nextScale));
            // Keeps the point under the cursor in place while the scale changes.
            view.translateX = centerX - (centerX - view.translateX) * (scale / view.scale);
            view.translateY = centerY - (centerY - view.translateY) * (scale / view.scale);
            view.scale = scale;
            apply();
        };
        const fit = (): void => {
            const viewport = root.getBoundingClientRect();
            const box = layer.getBoundingClientRect();
            const contentWidth = box.width / view.scale;
            const contentHeight = box.height / view.scale;
            if (!contentWidth || !contentHeight) {
                view.scale = 1;
                view.translateX = 0;
                view.translateY = 0;
                apply();
                return;
            }
            const scale = Math.min(viewport.width / contentWidth, viewport.height / contentHeight, 1) * 0.92;
            view.scale = Math.max(minScale, Math.min(maxScale, scale));
            view.translateX = (viewport.width - contentWidth * view.scale) / 2;
            view.translateY = (viewport.height - contentHeight * view.scale) / 2;
            apply();
        };

        const onWheel = (event: WheelEvent): void => {
            if (frozenRef.current) return;
            event.preventDefault();
            const bounds = root.getBoundingClientRect();
            // A pinch arrives as a wheel event with ctrlKey and needs a stronger factor.
            const factor = Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.0016));
            zoomAt(event.clientX - bounds.left, event.clientY - bounds.top, view.scale * factor);
        };

        let panning = false;
        let panPointerId = 0;
        let moved = false;
        let startX = 0;
        let startY = 0;
        let originX = 0;
        let originY = 0;
        const onPointerDown = (event: PointerEvent): void => {
            if (frozenRef.current || (event.target instanceof Element && event.target.closest(".pan-canvas-hud"))) return;
            panning = true;
            panPointerId = event.pointerId;
            moved = false;
            startX = event.clientX;
            startY = event.clientY;
            originX = view.translateX;
            originY = view.translateY;
        };
        const endPan = (event: PointerEvent): void => {
            if (!panning || event.pointerId !== panPointerId) return;
            panning = false;
            root.classList.remove("is-grabbing");
            try {
                root.releasePointerCapture(event.pointerId);
            } catch {
                // The capture may already be released.
            }
        };
        const onPointerMove = (event: PointerEvent): void => {
            if (!panning || event.pointerId !== panPointerId) return;
            // Until the pan captures the pointer, a release outside the canvas never reaches it; a move with no button
            // held shows that the press has ended.
            if (event.buttons === 0) {
                endPan(event);
                return;
            }
            const deltaX = event.clientX - startX;
            const deltaY = event.clientY - startY;
            if (!moved && Math.hypot(deltaX, deltaY) > DRAG_THRESHOLD_PX) {
                moved = true;
                // Captured only once the press becomes a pan: a captured pointer's click targets the canvas, so an
                // early capture would keep a plain click from reaching the button under the pointer.
                try {
                    root.setPointerCapture(event.pointerId);
                } catch {
                    // Not every environment implements pointer capture; the pan still follows the pointer.
                }
                root.classList.add("is-grabbing");
            }
            if (moved) {
                view.translateX = originX + deltaX;
                view.translateY = originY + deltaY;
                apply();
            }
        };
        const onClickCapture = (event: MouseEvent): void => {
            if (moved) {
                event.stopPropagation();
                event.preventDefault();
                moved = false;
            }
        };

        root.addEventListener("wheel", onWheel, {passive: false});
        root.addEventListener("pointerdown", onPointerDown);
        root.addEventListener("pointermove", onPointerMove);
        root.addEventListener("pointerup", endPan);
        root.addEventListener("pointercancel", endPan);
        root.addEventListener("click", onClickCapture, true);

        controllerRef.current = {
            fit,
            zoomStep: (direction: number): void => {
                const bounds = root.getBoundingClientRect();
                zoomAt(bounds.width / 2, bounds.height / 2, direction > 0 ? view.scale * 1.25 : view.scale / 1.25);
            },
            get view(): PanCanvasView {
                return {...view};
            },
        };

        apply();
        // Fits once the content has laid out.
        const frame = requestAnimationFrame(fit);

        return () => {
            cancelAnimationFrame(frame);
            root.removeEventListener("wheel", onWheel);
            root.removeEventListener("pointerdown", onPointerDown);
            root.removeEventListener("pointermove", onPointerMove);
            root.removeEventListener("pointerup", endPan);
            root.removeEventListener("pointercancel", endPan);
            root.removeEventListener("click", onClickCapture, true);
            controllerRef.current = null;
        };
    }, [minScale, maxScale, gridSize]);

    useImperativeHandle(ref, (): PanCanvasHandle => ({
        fit: () => controllerRef.current?.fit(),
        zoomStep: (direction: number) => controllerRef.current?.zoomStep(direction),
        get view(): PanCanvasView {
            return controllerRef.current?.view ?? {...viewRef.current};
        },
    }), []);

    return (
            <div
                    ref={rootRef}
                    className="pan-canvas"
                    data-grid={grid ? true : undefined}
                    data-static={frozen ? "true" : undefined}
                    style={{...TOKEN_VARIABLES, ...style}}
            >
                <div ref={layerRef} className="pan-canvas-layer" style={layerStyle}>
                    {children}
                </div>
                {hud && (
                        <div className="pan-canvas-hud">
                            {/* Disabled while frozen, so the freeze also holds for the keyboard. */}
                            <button data-zoom="out" type="button" aria-label={labels.zoomOut} disabled={frozen} onClick={() => controllerRef.current?.zoomStep(-1)}>
                                &minus;
                            </button>
                            <span className="pan-canvas-zoom" ref={zoomReadoutRef}>100%</span>
                            <button data-zoom="in" type="button" aria-label={labels.zoomIn} disabled={frozen} onClick={() => controllerRef.current?.zoomStep(1)}>
                                +
                            </button>
                            <button data-zoom="fit" type="button" disabled={frozen} onClick={() => controllerRef.current?.fit()}>
                                {labels.fit}
                            </button>
                        </div>
                )}
                {hint !== undefined && <div className="pan-canvas-hint">{hint}</div>}
            </div>
    );
});
